import { useMemo, useState } from "react";
import {
  cheapestOf,
  DEARER_REASONS,
  rankByUse,
  suggestPacks,
  type CreateSupplierOrder,
  type SupplierPrice,
} from "@dentacore/shared-types";

import { formatMoney } from "../patients/money";
import { Modal } from "../ui/modal";
import { describeNumberError, NumberField } from "../ui/number-field";
import { leadText, namesText, orderMessage, packUsesText, perUseText } from "./supplier-text";
import { useSeesPerUseCost } from "./use-sees-per-use-cost";
import inv from "./inventory.module.css";
import styles from "./suppliers.module.css";
import { RequiredMark } from "../ui/required-mark";

/** Whole packs, at least one. A bound, not a clinic's data. */
const PACKS = { min: 1, integer: true, unit: "packs" } as const;

export interface OrderableItem {
  id: string;
  name: string;
  quantityOnHand: number;
  parLevel: number | null;
  usesPerStockUnit: number;
}

/**
 * *Order it* — STORY-171. Opened from Home's notice, the popup and an item's
 * panel, so it is one component.
 *
 * - Suppliers are listed **cheapest per use first** (SU-D1). One clear cheapest
 *   is preselected; **a tie preselects nobody** and says the prices are the
 *   same, so the assistant picks.
 * - The amount starts at a refill to the full shelf (SU-D5) and is editable.
 * - A dearer supplier asks **why**, with three quick picks or free text, and
 *   will not save without one (SU-D7, SU-D12). The server refuses it too.
 * - DentaCore never sends the order (SU-D4): the dialog shows the message and
 *   the number to copy, and *Mark as ordered* records that it was sent.
 */
export function OrderDialog({
  item,
  prices,
  phones,
  currency,
  busy,
  onOrder,
  onClose,
}: {
  item: OrderableItem;
  /** Every live price for this item. */
  prices: readonly SupplierPrice[];
  /** Supplier id → phone, for the number to copy. */
  phones: ReadonlyMap<string, string | null>;
  currency: string;
  busy: boolean;
  /** Resolves true once the order is recorded, so the dialog closes only then. */
  onOrder: (body: CreateSupplierOrder) => Promise<boolean>;
  onClose: () => void;
}): React.JSX.Element {
  const ranked = useMemo(() => rankByUse(prices), [prices]);
  const cheapest = useMemo(() => cheapestOf(ranked), [ranked]);
  const tie = cheapest.length > 1;

  const [priceId, setPriceId] = useState<string | null>(
    cheapest.length === 1 ? (cheapest[0]?.id ?? null) : null,
  );
  const chosen = ranked.find((p) => p.id === priceId) ?? null;
  const [packs, setPacks] = useState(
    chosen === null ? "1" : String(suggestPacks(item, chosen.usesPerPack)),
  );
  const [reason, setReason] = useState("");
  // Price a use is a cost figure: only with Accounting (Emile, 2026-10-16).
  // Price per use needs Accounting and accounting.view (QF-D20).
  const hasAccounting = useSeesPerUseCost();

  const [copied, setCopied] = useState<string | null>(null);

  const dearer = chosen !== null && !cheapest.some((p) => p.id === chosen.id);
  const packCount = Number(packs);
  const packsValid = describeNumberError(packs, PACKS) === null && packs.trim() !== "";
  const best = cheapest[0];
  // What the same uses would have cost from the cheapest, in minor units.
  const extra =
    dearer && best !== undefined && packsValid
      ? Math.round(
          (chosen.priceCents * packCount * best.usesPerPack -
            best.priceCents * packCount * chosen.usesPerPack) /
            best.usesPerPack,
        )
      : 0;
  const blocked = chosen === null || !packsValid || (dearer && reason.trim() === "");

  const copy = (key: string, text: string): void => {
    // Refused in some browsers and frames; the text is selectable either way.
    void navigator.clipboard
      ?.writeText(text)
      .then(() => setCopied(key))
      .catch(() => setCopied(null));
  };

  const message =
    chosen === null
      ? null
      : orderMessage(chosen.supplierName, packCount || 1, item.name, chosen.pack);
  const phone = chosen === null ? null : (phones.get(chosen.supplierId) ?? null);

  return (
    <Modal label={`Order ${item.name}`} onClose={onClose} testId="order-dialog">
      <form
        className={inv.dialogForm}
        onSubmit={(event) => {
          event.preventDefault();
          if (blocked || chosen === null) return;
          void onOrder({
            inventoryItemId: item.id,
            supplierPriceId: chosen.id,
            packs: packCount,
            ...(dearer ? { reason: reason.trim() } : {}),
          }).then((landed) => {
            if (landed) onClose();
          });
        }}
      >
        <h2 className={inv.dialogTitle}>Order {item.name}</h2>
        <p className={styles.sub}>
          {item.quantityOnHand} uses left · a full shelf is {item.parLevel ?? item.usesPerStockUnit}
        </p>

        <div className={styles.options} role="radiogroup" aria-label="Supplier">
          {tie && (
            <p className={styles.note} data-testid="order-tie">
              {namesText(cheapest.map((p) => p.supplierName))} are the same price
              {hasAccounting ? " per use" : ""} — pick either.
            </p>
          )}
          {ranked.map((price) => {
            const on = price.id === priceId;
            const isCheapest = cheapest.some((p) => p.id === price.id);
            return (
              <label
                key={price.id}
                className={on ? `${styles.option} ${styles.optionOn}` : styles.option}
              >
                <input
                  type="radio"
                  name="order-supplier"
                  checked={on}
                  onChange={() => {
                    setPriceId(price.id);
                    setPacks(String(suggestPacks(item, price.usesPerPack)));
                  }}
                  data-testid={`order-pick-${price.supplierId}`}
                />
                <span>
                  <span className={styles.optionName}>{price.supplierName}</span>{" "}
                  {isCheapest && (
                    <span className={`${styles.chip} ${styles.chipBest}`}>
                      {tie ? "Same price" : "Cheapest"}
                    </span>
                  )}
                  <span className={styles.meta}>
                    {packUsesText(price)}
                    {leadText(price.leadDays) !== null && ` · ${leadText(price.leadDays) ?? ""}`}
                  </span>
                </span>
                <span className={styles.money}>
                  {formatMoney(price.priceCents, currency)}
                  {hasAccounting && (
                    <span className={styles.meta}>{perUseText(price, currency)} a use</span>
                  )}
                </span>
              </label>
            );
          })}
        </div>

        {chosen !== null && (
          <>
            {dearer && (
              <div className={inv.field}>
                {/* A per-use figure, so it needs what a price per use needs (QF-D20). */}
                {hasAccounting && best !== undefined && extra > 0 && (
                  <p className={`${styles.note} ${styles.warm}`}>
                    About {formatMoney(extra, currency)} more than the same uses from{" "}
                    {best.supplierName}.
                  </p>
                )}
                <span className={inv.fieldLabel}>
                  Why not the cheapest?
                  <RequiredMark />
                </span>
                <div className={styles.reasons}>
                  {DEARER_REASONS.map((pick) => (
                    <button
                      key={pick}
                      type="button"
                      className={reason === pick ? inv.primary : inv.ghost}
                      onClick={() => setReason(pick)}
                    >
                      {pick}
                    </button>
                  ))}
                </div>
                <textarea
                  className={inv.input}
                  rows={2}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Or write your own reason"
                  aria-label="Why not the cheapest"
                  data-testid="order-reason"
                />
              </div>
            )}

            <div className={inv.dialogGrid}>
              <NumberField
                label="Packs"
                required
                className={inv.field}
                labelClassName={inv.fieldLabel}
                inputClassName={inv.input}
                value={packs}
                onChange={setPacks}
                rules={PACKS}
                hint="Suggested: enough to get back to a full shelf."
                testId="order-packs"
              />
              <div className={inv.field}>
                <span className={inv.fieldLabel}>Adds</span>
                <span>{packsValid ? `${String(packCount * chosen.usesPerPack)} uses` : "—"}</span>
              </div>
            </div>

            <div className={inv.field}>
              <span className={inv.fieldLabel}>
                Send this to {chosen.supplierName}
                {phone !== null && ` · ${phone}`}
              </span>
              <p className={styles.message} data-testid="order-message">
                {message}
              </p>
              <div className={styles.copyRow}>
                <button
                  type="button"
                  className={inv.ghost}
                  onClick={() => copy("message", message ?? "")}
                >
                  {copied === "message" ? "Copied" : "Copy the message"}
                </button>
                {phone !== null && (
                  <button type="button" className={inv.quiet} onClick={() => copy("phone", phone)}>
                    {copied === "phone" ? "Copied" : "Copy the number"}
                  </button>
                )}
              </div>
            </div>

            <div className={styles.total}>
              <span>Total</span>
              <span>{packsValid ? formatMoney(chosen.priceCents * packCount, currency) : "—"}</span>
            </div>
          </>
        )}

        <div className={inv.dialogActions}>
          <button type="button" className={inv.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={inv.primary}
            disabled={busy || blocked}
            data-testid="order-save"
          >
            {chosen === null ? "Pick a supplier first" : "Mark as ordered"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
